import { useCallback, useEffect, useState } from 'react';

import { fetchRestaurantServices } from './restaurantApi';
import { extractEnabledServiceTypes } from './restaurantServiceAccess';
import { getActiveRestaurantId, isSupermarketContext } from './entityContext';

const CACHE_PREFIX = 'restaurantEnabledServices:';

function cacheKey(restaurantId) {
  return `${CACHE_PREFIX}${restaurantId}`;
}

function readCachedEnabledTypes(restaurantId) {
  if (!restaurantId) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(restaurantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeCachedEnabledTypes(restaurantId, types) {
  if (!restaurantId || !Array.isArray(types)) return;
  try {
    sessionStorage.setItem(cacheKey(restaurantId), JSON.stringify(types));
  } catch (e) {
    // ignore quota / private mode
  }
}

function getInitialEnabledTypes() {
  if (isSupermarketContext()) return null;
  const restaurantId = getActiveRestaurantId();
  return readCachedEnabledTypes(restaurantId);
}

export const RESTAURANT_SERVICES_CHANGED_EVENT = 'eats:restaurantServicesContextChanged';

export function notifyRestaurantServicesContextChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(RESTAURANT_SERVICES_CHANGED_EVENT));
}

export function useRestaurantEnabledServices() {
  const [restaurantId, setRestaurantId] = useState(() => getActiveRestaurantId());
  const [enabledTypes, setEnabledTypes] = useState(() => getInitialEnabledTypes());
  const [loading, setLoading] = useState(() => {
    if (isSupermarketContext()) return false;
    const id = getActiveRestaurantId();
    if (!id) return true;
    return readCachedEnabledTypes(id) == null;
  });

  useEffect(() => {
    const syncRestaurantId = () => {
      const nextId = getActiveRestaurantId();
      setRestaurantId((current) => (current === nextId ? current : nextId));
    };

    syncRestaurantId();
    window.addEventListener(RESTAURANT_SERVICES_CHANGED_EVENT, syncRestaurantId);
    return () => {
      window.removeEventListener(RESTAURANT_SERVICES_CHANGED_EVENT, syncRestaurantId);
    };
  }, []);

  const reload = useCallback(async () => {
    if (isSupermarketContext()) {
      setEnabledTypes(null);
      setLoading(false);
      return;
    }

    const activeRestaurantId = restaurantId || getActiveRestaurantId();
    if (!activeRestaurantId) {
      setEnabledTypes(null);
      setLoading(true);
      return;
    }

    const cached = readCachedEnabledTypes(activeRestaurantId);
    if (cached) {
      setEnabledTypes(cached);
    } else {
      setEnabledTypes(null);
    }

    setLoading(cached == null);
    try {
      const rows = await fetchRestaurantServices(activeRestaurantId);
      const types = extractEnabledServiceTypes(rows);
      setEnabledTypes(types);
      if (Array.isArray(types)) {
        writeCachedEnabledTypes(activeRestaurantId, types);
      }
    } catch (error) {
      console.warn('Could not load restaurant services:', error?.response?.data || error);
      if (!cached) setEnabledTypes(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { enabledTypes, loading, reload };
}
